/* Admin bookings: live-schema table, nurse + doctor assignment, create booking. */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import config from '../../config';
import { Search, XCircle, Plus, FileText, ExternalLink } from 'lucide-react';
import { fieldError, indianMobile10 } from '../../lib/validation';
import { pinServiceError, normalizePincode } from '../../lib/servicePincodes';
import { formatDateDMY, formatDateTimeDMY, ageFromDob } from '../../lib/dates';
import DmyDateInput from '../../components/DmyDateInput';
import { openAuthedFile, paymentSucceeded } from '../staff/staffShared';
import { useSortedPage, SortTh, PageBar } from '../../components/admin/DataTable';

const statusColors = {
  awaiting_payment: 'bg-amber-100 text-amber-800',
  pending_confirmation: 'bg-amber-100 text-amber-800',
  confirmed: 'bg-blue-100 text-blue-800', assigned: 'bg-purple-100 text-purple-800',
  on_the_way: 'bg-yellow-100 text-yellow-800', arrived: 'bg-orange-100 text-orange-800',
  ongoing: 'bg-orange-100 text-orange-700',
  completed: 'bg-green-100 text-green-800', cancelled: 'bg-red-100 text-red-800',
};
const statusLabels = {
  awaiting_payment: 'Awaiting payment', pending_confirmation: 'Pending', confirmed: 'Confirmed', assigned: 'Assigned',
  on_the_way: 'On the Way', arrived: 'Arrived', ongoing: 'Ongoing',
  completed: 'Completed', cancelled: 'Cancelled',
};
const statusFlow = ['confirmed', 'assigned', 'on_the_way', 'arrived', 'completed'];

function CreateBookingModal({ onClose, onCreated, getAuthHeaders }) {
  const [services, setServices] = useState([]);
  const [slots, setSlots] = useState([]);
  const [form, setForm] = useState({
    customer_name: '', customer_phone: '', service_id: '', service_name: '',
    beneficiary_name: '', beneficiary_age: '', beneficiary_dob: '', preferred_date: '', preferred_slot: '',
    address_text: '', pincode: '', payment_method: 'cash', prescription_status: 'not_required', notes: '', paid_offline: false,
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [servicePins, setServicePins] = useState([]);
  const [pinsLoaded, setPinsLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${config.API}/admin/services`, { headers: getAuthHeaders() })
      .then(({ data }) => setServices((data.items || []).filter(s => s.active !== false)))
      .catch(() => {});
    axios.get(`${config.API}/config/service-pincodes`)
      .then(({ data }) => setServicePins(Array.isArray(data.pincodes) ? data.pincodes : []))
      .catch(() => setServicePins([]))
      .finally(() => setPinsLoaded(true));
  }, [getAuthHeaders]);

  useEffect(() => {
    if (!form.preferred_date) return;
    axios.get(`${config.API}/availability/slots`, { params: { date: form.preferred_date } })
      .then(({ data }) => setSlots(data.slots || []))
      .catch(() => setSlots([]));
  }, [form.preferred_date]);

  const pickService = (id) => {
    const s = services.find(x => x.id === id);
    setForm({ ...form, service_id: id, service_name: s?.name || '' });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const err = fieldError({ name: form.customer_name, phone: form.customer_phone });
      if (err) { setError(err); return; }
      if (!(form.beneficiary_name || "").trim()) { setError("Patient name is required"); return; }
      if (!form.beneficiary_dob) { setError("Date of birth is required"); return; }
      const age = Number(ageFromDob(form.beneficiary_dob));
      if (!Number.isFinite(age) || age < 0 || age > 120) { setError("Enter a valid date of birth"); return; }
      if (!(form.address_text || "").trim()) { setError("Address is required"); return; }
      const pin = String(form.pincode || "").replace(/\D/g, "");
      const pinErr = pinServiceError(pin, servicePins, { listReady: pinsLoaded, requireComplete: true });
      if (pinErr) { setError(pinErr); return; }
      await axios.post(`${config.API}/admin/bookings`, { ...form, pincode: pin, beneficiary_age: age, beneficiary_dob: form.beneficiary_dob, customer_phone: indianMobile10(form.customer_phone) }, { headers: getAuthHeaders() });
      onCreated();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Could not create booking');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()}
        className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-3 max-h-[90vh] overflow-y-auto" data-testid="create-booking-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-[#311b52]" style={{ fontFamily: 'Poppins' }}>Create booking</h3>
          <button type="button" onClick={onClose}><XCircle className="w-5 h-5 text-[#673c96]" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-[#311b52] block mb-1">Customer name *</label>
            <input required value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm" data-testid="cb-customer-name" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#311b52] block mb-1">Customer phone *</label>
            <input required value={form.customer_phone} onChange={e => setForm({ ...form, customer_phone: e.target.value })}
              placeholder="10-digit" className="w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm" data-testid="cb-customer-phone" />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-[#311b52] block mb-1">Service / vaccine *</label>
          <select required value={form.service_id} onChange={e => pickService(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm bg-white" data-testid="cb-service">
            <option value="">Select…</option>
            {services.map(s => <option key={s.id} value={s.id}>{s.name}{s.indicative_price ? ` — ₹${s.indicative_price}` : ''}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-[#311b52] block mb-1">Patient / beneficiary name *</label>
            <input required value={form.beneficiary_name} onChange={e => setForm({ ...form, beneficiary_name: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm" data-testid="cb-beneficiary" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#311b52] block mb-1">Date of birth *</label>
            <DmyDateInput required noFuture value={form.beneficiary_dob} onChange={iso => setForm({ ...form, beneficiary_dob: iso, beneficiary_age: ageFromDob(iso) })}
              className="w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm" data-testid="cb-dob" />
            {ageFromDob(form.beneficiary_dob) !== "" && <p className="text-[11px] text-[#6b7280] mt-1">Age: {ageFromDob(form.beneficiary_dob)} years</p>}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-[#311b52] block mb-1">Date *</label>
            <DmyDateInput required min={new Date().toLocaleDateString("en-CA")} value={form.preferred_date} onChange={iso => setForm({ ...form, preferred_date: iso })}
              className="w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm" data-testid="cb-date" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#311b52] block mb-1">Slot *</label>
            <select required value={form.preferred_slot} onChange={e => setForm({ ...form, preferred_slot: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm bg-white" data-testid="cb-slot">
              <option value="">Select…</option>
              {slots.filter(s => s.status === 'available').map(s => (
                <option key={s.hour} value={s.hour}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-[#311b52] block mb-1">Address *</label>
          <textarea required rows={2} value={form.address_text} onChange={e => setForm({ ...form, address_text: e.target.value })}
            className="w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm" data-testid="cb-address" />
        </div>
        <div>
          <label className="text-xs font-medium text-[#311b52] block mb-1">PIN code *</label>
          <input required value={form.pincode} maxLength={6} inputMode="numeric"
            onChange={e => setForm({ ...form, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })}
            placeholder="122001"
            className={`w-full px-3 py-2 rounded-lg border text-sm ${
              normalizePincode(form.pincode).length === 6 && pinServiceError(form.pincode, servicePins, { listReady: pinsLoaded })
                ? "border-red-500 bg-red-50"
                : "border-[#673c96]/20"
            }`} data-testid="cb-pincode" />
          {normalizePincode(form.pincode).length === 6 && pinServiceError(form.pincode, servicePins, { listReady: pinsLoaded }) ? (
            <p className="text-xs text-red-600 mt-1">{pinServiceError(form.pincode, servicePins, { listReady: pinsLoaded })}</p>
          ) : null}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-[#311b52] block mb-1">Payment</label>
            <select value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm bg-white">
              {['cash', 'upi', 'card'].map(m => <option key={m} value={m}>{m.toUpperCase()}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-[#311b52] block mb-1">Prescription</label>
            <select value={form.prescription_status} onChange={e => setForm({ ...form, prescription_status: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm bg-white">
              <option value="not_required">Not required</option>
              <option value="need_review">Doctor call needed</option>
              <option value="not_sure">Not sure</option>
            </select>
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-[#311b52] block mb-1">Notes</label>
          <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
            className="w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm" />
        </div>
        <label className="flex items-start gap-2 text-sm text-[#311b52]">
          <input type="checkbox" checked={!!form.paid_offline} onChange={e => setForm({ ...form, paid_offline: e.target.checked })}
            className="mt-1" data-testid="cb-paid-offline" />
          <span>Payment received offline — confirm now and assign a nurse. Leave unchecked to keep this off nurse/doctor calendars until you mark it paid.</span>
        </label>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <button disabled={saving} className="w-full bg-[#f37721] text-white rounded-lg px-4 py-2.5 text-sm font-medium" data-testid="cb-submit">
          {saving ? 'Creating…' : 'Create booking (price auto-calculated)'}
        </button>
      </form>
    </div>
  );
}

export default function Bookings() {
  const { getAuthHeaders, user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin' || user?.user?.role === 'super_admin';
  const [bookings, setBookings] = useState([]);
  const [nurses, setNurses] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [markPaidBusy, setMarkPaidBusy] = useState(false);
  const [markPaidError, setMarkPaidError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const h = { headers: getAuthHeaders() };
      const [bRes, nRes, dRes] = await Promise.all([
        axios.get(`${config.API}/admin/bookings${filter ? `?status=${filter}` : ''}`, h),
        axios.get(`${config.API}/admin/staff`, { params: { role: 'nurse' }, ...h }),
        axios.get(`${config.API}/admin/staff`, { params: { role: 'doctor' }, ...h }),
      ]);
      setBookings(bRes.data.bookings || []);
      setNurses((nRes.data.items || []).filter(n => n.active));
      setDoctors((dRes.data.items || []).filter(d => d.active));
    } catch (err) { if (process.env.NODE_ENV === 'development') console.error(err); }
    finally { setLoading(false); }
  }, [getAuthHeaders, filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateBooking = async (id, update) => {
    try {
      await axios.patch(`${config.API}/admin/bookings/${id}`, update, { headers: getAuthHeaders() });
      fetchData();
      setSelected(null);
    } catch (err) { if (process.env.NODE_ENV === 'development') console.error(err); }
  };

  const assign = async (id, body) => {
    try {
      await axios.patch(`${config.API}/ops/bookings/${id}/assign`, body, { headers: getAuthHeaders() });
      fetchData();
      setSelected(null);
    } catch (err) { if (process.env.NODE_ENV === 'development') console.error(err); }
  };

  const markPaidOffline = async (id) => {
    setMarkPaidBusy(true);
    setMarkPaidError('');
    try {
      const { data } = await axios.post(`${config.API}/admin/bookings/${id}/mark-paid-offline`, {}, { headers: getAuthHeaders() });
      setSelected(data);
      fetchData();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setMarkPaidError(typeof d === 'string' ? d : 'Could not mark this booking as paid');
    } finally {
      setMarkPaidBusy(false);
    }
  };

  const name = (b) => b.beneficiary_name || b.recipient_name || b.customer_name || '—';
  const vaccine = (b) => b.service_name || b.vaccine_name || b.vaccine_name_snapshot || '—';
  const date = (b) => formatDateDMY(b.preferred_date || b.booking_date);
  const slot = (b) => b.preferred_slot || b.booking_slot || '—';
  const nurseName = (b) => b.nurse_name || b.nurse_name_snapshot || '—';
  const amount = (b) => {
    const v = b.final_amount ?? b.final_price ?? b.indicative_price;
    return v != null ? `₹${Number(v).toLocaleString('en-IN')}` : '—';
  };

  const filtered = bookings.filter(b =>
    !search ||
    name(b).toLowerCase().includes(search.toLowerCase()) ||
    (b.customer_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (b.customer_phone || '').includes(search) ||
    b.id?.toLowerCase().includes(search.toLowerCase()) ||
    vaccine(b).toLowerCase().includes(search.toLowerCase())
  );

  const table = useSortedPage(filtered, { defaultKey: 'created_at', defaultDir: 'desc', pageSize: 20 });

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#673c96] border-t-transparent" /></div>;

  return (
    <div data-testid="admin-bookings" className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#673c96]" />
          <input data-testid="booking-search" value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-[#673c96]/20 text-sm focus:border-[#673c96] focus:ring-1 focus:ring-[#673c96]/20 outline-none"
            placeholder="Search by name, phone, ID, or vaccine..." />
        </div>
        <select data-testid="booking-status-filter" value={filter} onChange={e => setFilter(e.target.value)}
          className="px-4 py-2.5 rounded-lg border border-[#673c96]/20 text-sm bg-white focus:border-[#673c96] outline-none">
          <option value="">All Statuses</option>
          {Object.entries(statusLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button onClick={() => setShowCreate(true)} data-testid="create-booking-btn"
          className="bg-[#f37721] text-white rounded-lg px-4 py-2.5 text-sm font-medium flex items-center gap-2">
          <Plus className="w-4 h-4" /> New booking
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-[#673c96]/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="bookings-table">
            <thead className="bg-[#fdfcff]">
              <tr className="border-b border-[#673c96]/10">
                <SortTh label="ID" k="id" sortKey={table.sortKey} sortDir={table.sortDir} onSort={table.toggleSort} />
                <SortTh label="Patient" k="beneficiary_name" sortKey={table.sortKey} sortDir={table.sortDir} onSort={table.toggleSort} />
                <SortTh label="Vaccine" k="service_name" sortKey={table.sortKey} sortDir={table.sortDir} onSort={table.toggleSort} />
                <SortTh label="Date" k="preferred_date" sortKey={table.sortKey} sortDir={table.sortDir} onSort={table.toggleSort} />
                <SortTh label="Created" k="created_at" sortKey={table.sortKey} sortDir={table.sortDir} onSort={table.toggleSort} />
                <SortTh label="Slot" k="preferred_slot" sortKey={table.sortKey} sortDir={table.sortDir} onSort={table.toggleSort} />
                <SortTh label="Amount" k="final_amount" sortKey={table.sortKey} sortDir={table.sortDir} onSort={table.toggleSort} />
                <SortTh label="Status" k="status" sortKey={table.sortKey} sortDir={table.sortDir} onSort={table.toggleSort} />
                <SortTh label="Nurse" k="nurse_name" sortKey={table.sortKey} sortDir={table.sortDir} onSort={table.toggleSort} />
                <th className="text-left py-3 px-4 text-xs font-semibold text-[#673c96]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {table.slice.map(b => {
                const sc = statusColors[b.status] || 'bg-gray-100 text-gray-800';
                return (
                  <tr key={b.id} className="border-b border-[#673c96]/5 hover:bg-[#fdfcff] transition-colors" data-testid={`booking-row-${b.id}`}>
                    <td className="py-3 px-4 font-mono text-xs text-[#673c96]">{(b.id || '').slice(0, 12)}</td>
                    <td className="py-3 px-4 font-medium text-[#311b52]">{name(b)}</td>
                    <td className="py-3 px-4 text-[#3e195e]">{vaccine(b)}</td>
                    <td className="py-3 px-4 text-[#673c96]">{date(b)}</td>
                    <td className="py-3 px-4 text-xs text-[#673c96]">{formatDateTimeDMY(b.created_at)}</td>
                    <td className="py-3 px-4 text-[#673c96] text-xs">{slot(b)}</td>
                    <td className="py-3 px-4 text-[#311b52] text-xs font-semibold">{amount(b)}</td>
                    <td className="py-3 px-4"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${sc}`}>{statusLabels[b.status] || b.status}</span></td>
                    <td className="py-3 px-4 text-xs text-[#3e195e]">{nurseName(b)}</td>
                    <td className="py-3 px-4">
                      <button onClick={() => { setSelected(b); setConfirmCancel(false); }} data-testid={`manage-btn-${b.id}`}
                        className="text-xs font-medium text-[#673c96] hover:text-[#f37721] transition-colors">Manage</button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={10} className="py-8 text-center text-sm text-[#673c96]">No bookings found</td></tr>}
            </tbody>
          </table>
        </div>
        <PageBar page={table.page} pages={table.pages} total={table.total} setPage={table.setPage} noun="bookings" />
      </div>

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="booking-detail-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#311b52]" style={{ fontFamily: 'Poppins' }}>Booking {selected.id}</h3>
              <button onClick={() => setSelected(null)} className="text-[#673c96] hover:text-[#311b52]"><XCircle className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3 text-sm mb-6">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-[#673c96] text-xs">Patient</span><p className="font-medium text-[#311b52]">{name(selected)}</p></div>
                <div><span className="text-[#673c96] text-xs">Vaccine</span><p className="font-medium text-[#311b52]">{vaccine(selected)}</p></div>
                <div><span className="text-[#673c96] text-xs">Customer</span><p className="font-medium text-[#311b52]">{selected.customer_name || '—'}<br /><span className="text-xs text-[#673c96]">+{selected.customer_phone || '—'}</span></p></div>
                <div><span className="text-[#673c96] text-xs">Date & slot</span><p className="font-medium text-[#311b52]">{date(selected)} · {slot(selected)}</p></div>
                <div className="col-span-2 rounded-xl border border-[#673c96]/10 bg-[#fdfcff] p-3">
                  <span className="text-[#673c96] text-xs">Payment</span>
                  <p className="font-medium text-[#311b52]">{amount(selected)} · {(selected.payment_status || 'pending').replaceAll('_', ' ')}</p>
                  {selected.payment_method ? <p className="text-xs text-[#673c96] mt-0.5">Method: {selected.payment_method}</p> : null}
                  {selected.razorpay_payment_id ? <p className="text-[11px] font-mono text-[#673c96] mt-1">Payment ID: {selected.razorpay_payment_id}</p> : null}
                  {selected.razorpay_order_id ? <p className="text-[11px] font-mono text-[#9ca3af]">Order ID: {selected.razorpay_order_id}</p> : null}
                  {selected.paid_at ? <p className="text-xs text-[#673c96] mt-0.5">Paid at {String(selected.paid_at).slice(0, 16).replace('T', ' ')}</p> : null}
                  {isSuperAdmin && !paymentSucceeded(selected.payment_status) && (selected.cancel_reason || '') !== 'customer_cancelled' ? (
                    <button type="button" disabled={markPaidBusy} onClick={() => markPaidOffline(selected.id)}
                      className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold"
                      data-testid="mark-paid-offline">
                      {markPaidBusy ? 'Marking…' : 'Mark paid offline'}
                    </button>
                  ) : null}
                  {markPaidError ? <p className="text-xs text-red-600 mt-1">{markPaidError}</p> : null}
                  <div className="flex flex-wrap gap-2 mt-2">
                    <button type="button"
                      onClick={() => openAuthedFile(`${config.API}/admin/bookings/${selected.id}/invoice`, null, getAuthHeaders(), { allowHtml: true, filenameFallback: 'invoice' })}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#673c96] text-white text-xs font-semibold">
                      <FileText className="w-3.5 h-3.5" /> View invoice / receipt
                    </button>
                    {selected.razorpay_payment_id && String(selected.razorpay_payment_id).startsWith('pay_') ? (
                      <a href={`https://dashboard.razorpay.com/app/payments/${selected.razorpay_payment_id}`} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#f37721]/40 text-[#f37721] text-xs font-semibold">
                        <ExternalLink className="w-3.5 h-3.5" /> Razorpay
                      </a>
                    ) : null}
                  </div>
                </div>
                <div><span className="text-[#673c96] text-xs">Prescription</span><p className="font-medium text-[#311b52] capitalize">{(selected.prescription_status || '—').replaceAll('_', ' ')}</p></div>
                {(selected.prescription_file_reference || selected.patient_prescription_file_reference) && (
                  <div className="col-span-2 flex flex-wrap gap-2">
                    <button type="button"
                      onClick={() => openAuthedFile(`${config.API}/admin/bookings/${selected.id}/prescription`, null, getAuthHeaders())}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#673c96] text-white text-xs font-semibold">
                      <FileText className="w-3.5 h-3.5" /> Open prescription
                    </button>
                    {selected.patient_prescription_file_reference && (
                      <button type="button"
                        onClick={() => openAuthedFile(`${config.API}/admin/bookings/${selected.id}/prescription?kind=patient`, null, getAuthHeaders())}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#673c96]/30 text-[#673c96] text-xs font-semibold">
                        Patient upload
                      </button>
                    )}
                  </div>
                )}
                <div className="col-span-2"><span className="text-[#673c96] text-xs">Address</span><p className="font-medium text-[#311b52]">{selected.address || '—'}</p></div>
                {selected.notes && <div className="col-span-2"><span className="text-[#673c96] text-xs">Notes</span><p className="font-medium text-[#311b52]">{selected.notes}</p></div>}
                <div><span className="text-[#673c96] text-xs">Nurse</span><p className="font-medium text-[#311b52]">{nurseName(selected)}</p></div>
                <div><span className="text-[#673c96] text-xs">Doctor</span><p className="font-medium text-[#311b52]">{selected.doctor_name || '—'}</p></div>
              </div>
            </div>

            {selected.status !== 'completed' && selected.status !== 'cancelled' && (
              <div className="space-y-4 border-t border-[#673c96]/10 pt-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-[#311b52] block mb-1">Assign nurse</label>
                    <select data-testid="assign-nurse-select" onChange={e => e.target.value && assign(selected.id, { nurse_id: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm bg-white" defaultValue="">
                      <option value="" disabled>Select a nurse</option>
                      {nurses.map(n => (
                        <option key={n.id} value={n.id}>{n.full_name}{n.profile?.qualification ? ` — ${n.profile.qualification}` : ''}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#311b52] block mb-1">Assign doctor</label>
                    <select data-testid="assign-doctor-select" onChange={e => e.target.value && assign(selected.id, { doctor_id: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm bg-white" defaultValue="">
                      <option value="" disabled>Select a doctor</option>
                      {doctors.map(d => <option key={d.id} value={d.id}>{d.full_name}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-[#311b52] block mb-1">Update status</label>
                  <div className="flex flex-wrap gap-2">
                    {statusFlow.filter(s => s !== selected.status).map(s => (
                      <button key={s} onClick={() => updateBooking(selected.id, { status: s })}
                        data-testid={`set-status-${s}`}
                        className={`text-xs font-semibold rounded-full px-3 py-1 ${statusColors[s]} hover:opacity-80 transition-opacity`}>
                        {statusLabels[s]}
                      </button>
                    ))}
                  </div>
                </div>
                {confirmCancel ? (
                  <div className="rounded-xl border border-red-200 bg-red-50 p-4 space-y-3" data-testid="cancel-confirm-box">
                    <p className="text-sm font-semibold text-red-800">Cancel this booking for {name(selected)}? This cannot be undone.</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => { updateBooking(selected.id, { status: 'cancelled' }); setConfirmCancel(false); }}
                        data-testid="cancel-booking-confirm"
                        className="flex-1 py-3 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold">
                        Yes, cancel booking
                      </button>
                      <button type="button" onClick={() => setConfirmCancel(false)}
                        className="flex-1 py-3 rounded-lg border border-[#673c96]/20 text-[#311b52] text-sm font-semibold">
                        Keep booking
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmCancel(true)}
                    data-testid="cancel-booking-btn"
                    className="w-full py-3 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold transition-colors">
                    Cancel Booking
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {showCreate && (
        <CreateBookingModal getAuthHeaders={getAuthHeaders} onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchData(); }} />
      )}
    </div>
  );
}
